/**
 * The "couldn't load the recruiter list" surface, shared by both consumers of
 * `useImportOwners`.
 *
 * A failed owners fetch is indistinguishable from "no colleague has imports" if
 * it is swallowed — the scope menu simply loses every recruiter. See
 * `useImportOwners` for why that matters.
 *
 * Two house rules this follows, both learned elsewhere and written down in
 * `SearchDetailPage.tsx:365-387`:
 *
 *   - the live region wraps the MESSAGE only and the button is a SIBLING.
 *     `role="alert"` is implicitly `aria-atomic`, so a button inside it
 *     re-announces the whole sentence every time its label flips on `busy`.
 *   - neighbouring retries are disambiguated by distinct VISIBLE labels, not
 *     `aria-label` — which also fixes the sighted-user case that two identical
 *     "Retry" buttons create.
 */
import type { UseQueryResult } from "@tanstack/react-query";

import { RetryButton } from "../../../components/RetryButton";

export interface ImportOwnersErrorProps {
  /** The `useImportOwners` query — only `isError`/`isFetching`/`refetch` are read. */
  query: Pick<UseQueryResult<unknown>, "isError" | "isFetching" | "refetch">;
  /**
   * `inline` sits in a filter row (the picker); `banner` is the full-width
   * bordered notice the Imports page stacks with its other banners.
   */
  variant?: "inline" | "banner";
}

export function ImportOwnersError({ query, variant = "inline" }: ImportOwnersErrorProps) {
  if (!query.isError) return null;

  const isBanner = variant === "banner";

  return (
    <div
      data-testid="import-owners-error"
      className={
        isBanner
          ? "flex flex-wrap items-center gap-3 rounded-md border border-error-strong bg-error-muted px-4 py-2 text-body text-error"
          : "flex flex-wrap items-center gap-2 text-body text-error"
      }
    >
      <span role="alert">
        {isBanner
          ? "Couldn’t load the recruiter list — the “Imported by” filter is showing you and everyone only."
          : "Couldn’t load the recruiter list."}
      </span>
      <RetryButton
        busy={query.isFetching}
        tone="error"
        label="Retry recruiters"
        busyLabel="Retrying…"
        onClick={() => void query.refetch()}
      />
    </div>
  );
}
